// src/views/Features.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { publicSiteWebService } from "../services/publicSiteWebService";

export default function Features() {
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [pageData, setPageData] = useState({
    parametres: null,
    fonctionnalites: [],
    statistiques: [],
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await publicSiteWebService.getPublicData();
        setPageData({
          parametres: data.parametres,
          fonctionnalites: data.fonctionnalites || [],
          statistiques: data.statistiques || [],
        });
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const categories = [
    { id: "all", label: "Toutes" },
    { id: "gestion", label: "Gestion" },
    { id: "communication", label: "Communication" },
    { id: "suivi", label: "Suivi" },
    { id: "securite", label: "Sécurité" },
  ];

  const categoryColors = {
    gestion: "#004080",
    communication: "#0066CC",
    suivi: "#1a6fff",
    securite: "#0f6e56",
  };

  const features = pageData.fonctionnalites;
  const params = pageData.parametres || {};
  const primaryColor = params.couleur_primaire || "#004080";
  const secondaryColor = params.couleur_secondaire || "#0066CC";

  // Statistiques affichées
  const stats = pageData.statistiques.filter(s => s.actif).slice(0, 4);
  
  // Si pas de stats du backend, utiliser des stats par défaut
  const defaultStats = [
    { valeur: "10K+", libelle: "Utilisateurs actifs" },
    { valeur: "98%", libelle: "Taux de satisfaction" },
    { valeur: "24/7", libelle: "Support disponible" },
    { valeur: "50+", libelle: "Fonctionnalités" },
  ];
  
  const displayedStats = stats.length > 0 ? stats : defaultStats;

  const filteredFeatures = activeCategory === "all" 
    ? features.filter(f => f.actif !== false)
    : features.filter(f => f.categorie === activeCategory && f.actif !== false);

  if (loading) {
    return (
      <div className="features-page">
        <PublicTopBar />
        <div className="loader-container" style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div className="loader-spinner" style={{ width: 48, height: 48, border: "4px solid #e2e8f0", borderTopColor: primaryColor, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="features-page">
      <PublicTopBar />

      {/* Hero Section */}
      <section className="features-hero">
        <div className="features-hero-bg" />
        <div className="features-hero-container">
          <div className="features-hero-content">
            <span className="features-hero-badge">
              <i className="ti ti-rocket" />
              Découvrez toutes nos fonctionnalités
            </span>
            <h1 className="features-hero-title">
              Une solution complète pour<br />
              <span className="features-hero-accent" style={{ color: primaryColor }}>
                l'éducation moderne
              </span>
            </h1>
            <p className="features-hero-desc">
              {params.nom_plateforme || "Enumera"} réunit tous les outils nécessaires pour une gestion scolaire 
              efficace et une communication fluide entre tous les acteurs.
            </p>
            <div className="features-hero-actions">
              <Link to="/contact" className="btn btn-primary btn-lg" style={{ 
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              }}>
                <i className="ti ti-user-plus" />
                Commencer maintenant
              </Link>
              <Link to="/demo" className="btn btn-ghost btn-lg">
                <i className="ti ti-video" />
                Voir la démo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="features-stats">
        <div className="features-stats-container">
          {displayedStats.map((stat, index) => (
            <div key={index} className="features-stat">
              <span className="features-stat-value">{stat.valeur}</span>
              <span className="features-stat-label">{stat.libelle}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="features-grid-section">
        <div className="features-grid-container">
          <div className="features-grid-header">
            <h2 className="features-grid-title">
              Toutes les fonctionnalités à portée de main
            </h2>
            <p className="features-grid-desc">
              Explorez l'ensemble des outils conçus pour simplifier la gestion scolaire
            </p>
          </div>

          {/* Categories Filter */}
          <div className="features-categories">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`features-cat-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                style={activeCategory === cat.id ? {
                  background: primaryColor,
                  borderColor: primaryColor,
                  color: '#ffffff'
                } : {}}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Features Grid */}
          <div className="features-grid">
            {filteredFeatures.length > 0 ? (
              filteredFeatures.map((feature, index) => (
                <div 
                  key={feature.id || index} 
                  className="feature-card"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="feature-card-icon" style={{ 
                    background: `${feature.couleur || categoryColors[feature.categorie] || primaryColor}15`, 
                    color: feature.couleur || categoryColors[feature.categorie] || primaryColor
                  }}>
                    <i className={`ti ${feature.icone || 'ti-box'}`} />
                  </div>
                  <h3 className="feature-card-title">{feature.titre}</h3>
                  <p className="feature-card-desc">{feature.description}</p>
                </div>
              ))
            ) : (
              <div className="features-empty">
                <i className="ti ti-search" />
                <p>Aucune fonctionnalité trouvée dans cette catégorie</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="features-cta">
        <div className="features-cta-container" style={{
          background: `linear-gradient(135deg, ${primaryColor}08, ${secondaryColor}06)`
        }}>
          <div className="features-cta-content">
            <h2 className="features-cta-title">
              Prêt à transformer votre établissement ?
            </h2>
            <p className="features-cta-desc">
              Rejoignez des milliers d'établissements qui utilisent déjà {params.nom_plateforme || "Enumera"}
            </p>
            <div className="features-cta-actions">
              <Link to="/contact" className="btn btn-primary btn-lg" style={{ 
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              }}>
                <i className="ti ti-rocket" />
                Commencer maintenant
              </Link>
              <Link to="/demo" className="btn btn-ghost btn-lg">
                <i className="ti ti-video" />
                Voir la démo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

        .features-page {
          min-height: 100vh;
          background: #ffffff;
          color: #0f172a;
        }

        /* === HERO SECTION === */
        .features-hero {
          position: relative;
          padding: 120px 32px 80px;
          background: linear-gradient(135deg, #f8fafc 0%, #e6eef7 100%);
          overflow: hidden;
        }

        .features-hero-bg {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 30% 50%, rgba(0, 64, 128, 0.05) 0%, transparent 60%),
            radial-gradient(ellipse at 70% 50%, rgba(0, 102, 204, 0.03) 0%, transparent 60%);
          pointer-events: none;
        }

        .features-hero-container {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
        }

        .features-hero-content {
          text-align: center;
        }

        .features-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: rgba(0, 64, 128, 0.08);
          border: 1px solid rgba(0, 64, 128, 0.15);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
          color: #004080;
          margin-bottom: 16px;
        }

        .features-hero-title {
          font-size: 48px;
          font-weight: 800;
          line-height: 1.1;
          margin: 0 0 16px;
          letter-spacing: -0.02em;
          color: #0f172a;
        }

        .features-hero-accent {
          color: #004080;
        }

        .features-hero-desc {
          font-size: 18px;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto 32px;
          line-height: 1.6;
        }

        .features-hero-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 15px;
          text-decoration: none;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: linear-gradient(135deg, #004080, #0066CC);
          color: #ffffff;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 64, 128, 0.3);
        }

        .btn-ghost {
          background: rgba(15, 23, 42, 0.04);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-ghost:hover {
          background: rgba(15, 23, 42, 0.08);
        }

        .btn-lg {
          padding: 14px 32px;
          font-size: 16px;
        }

        /* === STATS SECTION === */
        .features-stats {
          padding: 40px 32px;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
        }

        .features-stats-container {
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }

        .features-stat {
          text-align: center;
        }

        .features-stat-value {
          display: block;
          font-size: 32px;
          font-weight: 700;
          color: #004080;
        }

        .features-stat-label {
          display: block;
          font-size: 14px;
          color: #94a3b8;
          margin-top: 4px;
        }

        /* === FEATURES GRID SECTION === */
        .features-grid-section {
          padding: 80px 32px;
          background: #f8fafc;
        }

        .features-grid-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .features-grid-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .features-grid-title {
          font-size: 36px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #0f172a;
        }

        .features-grid-desc {
          font-size: 16px;
          color: #64748b;
          margin: 0;
        }

        /* Categories */
        .features-categories {
          display: flex;
          gap: 8px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 40px;
        }

        .features-cat-btn {
          padding: 8px 20px;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          background: #ffffff;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .features-cat-btn:hover {
          background: rgba(0, 64, 128, 0.04);
          border-color: #004080;
          color: #004080;
        }

        .features-cat-btn.active {
          background: #004080;
          border-color: #004080;
          color: #ffffff;
        }

        /* Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .feature-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 28px 24px;
          transition: all 0.3s ease;
          animation: fadeUp 0.5s ease both;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
          border-color: rgba(0, 64, 128, 0.15);
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .feature-card-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 16px;
        }

        .feature-card-title {
          font-size: 17px;
          font-weight: 600;
          margin: 0 0 8px;
          color: #0f172a;
        }

        .feature-card-desc {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        .features-empty {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px 20px;
        }

        .features-empty i {
          font-size: 48px;
          color: #e2e8f0;
        }

        .features-empty p {
          color: #94a3b8;
          margin: 12px 0 0;
        }

        /* === CTA SECTION === */
        .features-cta {
          padding: 80px 32px;
          background: #ffffff;
        }

        .features-cta-container {
          max-width: 1280px;
          margin: 0 auto;
          background: linear-gradient(135deg, #e6eef7, #f0f4ff);
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          padding: 56px 40px;
          text-align: center;
        }

        .features-cta-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 12px;
          color: #0f172a;
        }

        .features-cta-desc {
          font-size: 16px;
          color: #64748b;
          margin: 0 0 24px;
        }

        .features-cta-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* === RESPONSIVE === */
        @media (max-width: 1024px) {
          .features-hero-title {
            font-size: 36px;
          }

          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .features-hero {
            padding: 100px 20px 60px;
          }

          .features-hero-title {
            font-size: 30px;
          }

          .features-stats {
            padding: 30px 20px;
          }

          .features-stats-container {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .features-stat-value {
            font-size: 24px;
          }

          .features-grid-section {
            padding: 60px 20px;
          }

          .features-grid-title {
            font-size: 28px;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .features-cat-btn {
            padding: 6px 16px;
            font-size: 13px;
          }

          .features-cta {
            padding: 60px 20px;
          }

          .features-cta-container {
            padding: 36px 24px;
          }

          .features-cta-title {
            font-size: 26px;
          }
        }

        @media (max-width: 480px) {
          .features-hero-title {
            font-size: 24px;
          }

          .features-hero-desc {
            font-size: 15px;
          }

          .features-stats-container {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }

          .features-stat-value {
            font-size: 20px;
          }

          .features-grid-title {
            font-size: 24px;
          }

          .feature-card {
            padding: 20px 16px;
          }
        }
      `}</style>
    </div>
  );
}